import { useEffect, useSyncExternalStore } from 'react';
import { REFRESH_MS } from '@constants/timing';
import { alertPaths, api, errorText, type AlertSummary } from '@lib/api';
import { ALERT_TYPES_LIVE, useStreamRefresh } from '@lib/stream';

/**
 * GET /alerts/summary, once for the whole app: the bell and the page's tabs read the same counts, so a "Tindak
 * lanjuti" on the page lowers the badge in the same moment. A refresh while one is on its way runs once more after it
 * (never two at a time, never a stale answer last). Forgotten when nothing reads it any more (signed out); an answer
 * that arrives after that belongs to the old session (`generation`) and is dropped.
 */
interface Snapshot { data: AlertSummary | null; error: string | null }
const EMPTY: Snapshot = { data: null, error: null };
let snap: Snapshot = EMPTY;
let inflight = false;
let again = false;
let readers = 0;
let generation = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function refreshAlertSummary() {
  if (!readers) return;
  if (inflight) {
    again = true;
    return;
  }
  inflight = true;
  const mine = generation;
  api<AlertSummary>(alertPaths.summary)
    .then(
      (data) => mine === generation && (snap = { data, error: null }),
      (e) => mine === generation && (snap = { ...snap, error: errorText(e) }),
    )
    .finally(() => {
      inflight = false;
      emit();
      if (again && readers) {
        again = false;
        refreshAlertSummary();
      }
    });
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  readers++;
  return () => {
    listeners.delete(fn);
    readers--;
    if (!readers) {
      snap = EMPTY;
      again = false;
      generation++;
    }
  };
}
const read = () => snap;

/**
 * The counts of the caller's scope (null until the first answer), fetched when a reader mounts. `live`: this reader
 * also refetches on live alert messages (the bell, always mounted in the shell: one subscription for the whole app).
 */
export function useAlertSummary({ live = false }: { live?: boolean } = {}) {
  const s = useSyncExternalStore(subscribe, read);
  useEffect(refreshAlertSummary, []);
  useStreamRefresh(ALERT_TYPES_LIVE, () => live && refreshAlertSummary(), REFRESH_MS.alertBadge);
  return { ...s, retry: refreshAlertSummary };
}
